import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import type { HotelRoomOptionsResult } from 'tripPlanner/api/types'

import { getCancellationText } from './ChatHotelRoomOptionCancellationPolicy'

type RoomOption = HotelRoomOptionsResult['roomOptions'][number]

interface Props {
  room: RoomOption
  checkIn?: string
}

/**
 * Room-level detail behind the "View amenities & policies" link.
 *
 * The room-options endpoint carries no room description or house rules, so the
 * "policies" half is assembled from the distinct cancellation policies across
 * the room's rates — the only policy text this payload actually has. A rate
 * whose policy we cannot describe contributes nothing rather than a placeholder.
 */
function ChatHotelRoomAmenitiesModal({ room, checkIn }: Props) {
  const intl = useIntl()

  const amenityInclusions = useMemo<Array<App.PackageInclusion>>(
    () => room.amenities.map((amenity, index) => ({
      id: `${amenity.name}-${index}`,
      description: amenity.name,
      symbol: determineOfferFeatureSymbol(amenity.icon ?? amenity.name),
    })),
    [room.amenities],
  )

  const cancellationTexts = useMemo(() => {
    const texts = room.rates
      .map((rate) => getCancellationText(rate.cancellationPolicy, intl, checkIn))
      .filter((text): text is string => !!text)
    return Array.from(new Set(texts))
  }, [checkIn, intl, room.rates])

  return (
    <Modal
      title={intl.formatMessage({
        id: 'nxhZ2VKfnjvd-dcCWOoT',
        defaultMessage: 'Amenities & policies',
        description: 'Title of the modal listing a hotel room’s amenities and cancellation policies in trip planner chat',
      })}
    >
      <Group direction="vertical" gap={20}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6">{room.roomName}</Heading>
          {room.bedGroupDescriptions.length > 0 && (
            <BodyText variant="medium" colour="neutral-two">
              {room.bedGroupDescriptions.join(' · ')}
            </BodyText>
          )}
        </VerticalSpacer>

        {amenityInclusions.length > 0 && (
          <VerticalSpacer gap={8}>
            <Heading variant="heading6">
              {intl.formatMessage({
                id: 'CHnPlMcircZn4JaSVCpT',
                defaultMessage: 'Room amenities',
                description: 'Heading of the amenities section in the trip planner chat room amenities modal',
              })}
            </Heading>
            <VerticalSpacer gap={4}>
              {amenityInclusions.map((amenity) => (
                <OfferInclusionItem key={amenity.id} inclusion={amenity} />
              ))}
            </VerticalSpacer>
          </VerticalSpacer>
        )}

        {cancellationTexts.length > 0 && (
          <VerticalSpacer gap={8}>
            <Heading variant="heading6">
              {intl.formatMessage({
                id: 'Q8hgAUh5s_aLEWsPjnes',
                defaultMessage: 'Cancellation policies',
                description: 'Heading of the cancellation policy section in the trip planner chat room amenities modal',
              })}
            </Heading>
            <VerticalSpacer gap={4}>
              {cancellationTexts.map((text) => (
                <BodyText key={text} variant="medium" as="p">{text}</BodyText>
              ))}
            </VerticalSpacer>
          </VerticalSpacer>
        )}
      </Group>
    </Modal>
  )
}

export default ChatHotelRoomAmenitiesModal
